/**
 * INYRON Quantum Vacuum Runtime
 *
 * Bounded simulation substrate over Ω-Spectrum. This models emergent field
 * state, attractors and spatial cells; it is not a claim of literal quantum
 * mechanics running at gameplay frame rate.
 */
import type {
  EmergentSpectralMode,
  SpectralEmitter,
  SpectrumVec3,
} from "./omega-spectrum";
import type { OmegaEffectChannel } from "./omega-genome";

export type VacuumPhaseTransitionKind =
  | "coherence-lock"
  | "entropy-release"
  | "cascade-bloom"
  | "symmetry-break"
  | "resonance-lock"
  | "vacuum-quiet";

export interface QuantumVacuumAttractor {
  id: string;
  bucket: number;
  centroidFrequency: number;
  strength: number;
  coherence: number;
  stability: number;
  evidence: number;
  firstSeenAt: number;
  lastSeenAt: number;
  modeIds: string[];
}

export interface QuantumVacuumCell {
  id: string;
  center: SpectrumVec3;
  energy: number;
  coherence: number;
  entropy: number;
  nonlinearPressure: number;
  sourceCount: number;
  lastSeenAt: number;
}

export interface QuantumVacuumState {
  atSeconds: number;
  energyDensity: number;
  coherence: number;
  entropy: number;
  resonanceDensity: number;
  noveltyFlux: number;
  cascadePressure: number;
  phaseAlignment: number;
  symmetryBreak: number;
  attractorStrength: number;
  activeModes: number;
  activeAttractors: number;
  activeCells: number;
}

export interface QuantumVacuumTransition {
  id: string;
  kind: VacuumPhaseTransitionKind;
  atSeconds: number;
  intensity: number;
  from: QuantumVacuumState;
  to: QuantumVacuumState;
}

export interface QuantumVacuumPresentationSignal {
  strength: number;
  pulse: number;
  turbulence: number;
  memory: number;
}

export interface QuantumVacuumProposal {
  channel: OmegaEffectChannel;
  effect: string;
  intensity: number;
  confidence: number;
  reason: string;
}

export interface QuantumVacuumSnapshot {
  version: 1;
  state: QuantumVacuumState;
  attractors: QuantumVacuumAttractor[];
  cells: QuantumVacuumCell[];
  transitionSerial: number;
  lastStateEmitAt: number;
  initialized: boolean;
}

export interface QuantumVacuumOptions {
  maxModesPerPass: number;
  maxEmittersPerPass: number;
  maxAttractors: number;
  maxCells: number;
  cellSize: number;
  attractorResolutionOctaves: number;
  attractorTtlSeconds: number;
  cellTtlSeconds: number;
  stateEmitIntervalSeconds: number;
  minAttractorStability: number;
  minAttractorOccurrences: number;
  onEvent?: (event: QuantumVacuumEvent) => void;
}

export interface QuantumVacuumUpdateInput {
  atSeconds: number;
  modes: readonly EmergentSpectralMode[];
  emitters?: readonly SpectralEmitter[];
}

export interface QuantumVacuumEvent {
  type:
    | "vacuum.state"
    | "vacuum.attractor.discovered"
    | "vacuum.attractor.reinforced"
    | "vacuum.phase-transition";
  atSeconds: number;
  payload:
    | QuantumVacuumState
    | QuantumVacuumAttractor
    | QuantumVacuumTransition;
}

const finite = (value: unknown, fallback = 0) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;
const clamp01 = (value: number) => Math.max(0, Math.min(1, finite(value)));
const positive = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : fallback;

function hash32(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function initialState(): QuantumVacuumState {
  return {
    atSeconds: 0,
    energyDensity: 0,
    coherence: 0,
    entropy: 0,
    resonanceDensity: 0,
    noveltyFlux: 0,
    cascadePressure: 0,
    phaseAlignment: 0,
    symmetryBreak: 0,
    attractorStrength: 0,
    activeModes: 0,
    activeAttractors: 0,
    activeCells: 0,
  };
}

function cloneState(state: QuantumVacuumState): QuantumVacuumState {
  return { ...state };
}

function cloneAttractor(value: QuantumVacuumAttractor): QuantumVacuumAttractor {
  return { ...value, modeIds: [...value.modeIds] };
}

function cloneCell(value: QuantumVacuumCell): QuantumVacuumCell {
  return { ...value, center: { ...value.center } };
}

export function deriveQuantumVacuumPresentationSignal(
  state: QuantumVacuumState,
  cell?: QuantumVacuumCell,
): QuantumVacuumPresentationSignal {
  const global = clamp01(
    state.energyDensity * 0.28 +
    state.coherence * 0.16 +
    state.cascadePressure * 0.20 +
    state.attractorStrength * 0.18 +
    state.phaseAlignment * 0.18
  );
  const spatial = cell
    ? clamp01(
      cell.energy * 0.50 +
      cell.nonlinearPressure * 0.30 +
      cell.coherence * 0.20
    )
    : 0;
  const rawStrength = clamp01(global * 0.68 + spatial * 0.32);
  return {
    strength: clamp01((rawStrength - 0.18) / 0.82),
    pulse: clamp01(
      state.phaseAlignment * 0.55 +
      state.resonanceDensity * 0.45
    ),
    turbulence: clamp01(
      state.entropy * 0.50 +
      state.symmetryBreak * 0.30 +
      state.cascadePressure * 0.20
    ),
    memory: clamp01(state.attractorStrength),
  };
}

export class QuantumVacuumRuntime {
  readonly options: QuantumVacuumOptions;
  private readonly attractors = new Map<string, QuantumVacuumAttractor>();
  private readonly cells = new Map<string, QuantumVacuumCell>();
  private state: QuantumVacuumState = initialState();
  private transitionSerial = 0;
  private lastStateEmitAt = -1e15;
  private initialized = false;

  constructor(options: Partial<QuantumVacuumOptions> = {}) {
    this.options = {
      maxModesPerPass: Math.max(8, Math.min(256, Math.trunc(positive(options.maxModesPerPass, 64)))),
      maxEmittersPerPass: Math.max(8, Math.min(1024, Math.trunc(positive(options.maxEmittersPerPass, 256)))),
      maxAttractors: Math.max(8, Math.min(256, Math.trunc(positive(options.maxAttractors, 64)))),
      maxCells: Math.max(8, Math.min(512, Math.trunc(positive(options.maxCells, 128)))),
      cellSize: positive(options.cellSize, 12),
      attractorResolutionOctaves: positive(options.attractorResolutionOctaves, 0.125),
      attractorTtlSeconds: positive(options.attractorTtlSeconds, 600),
      cellTtlSeconds: positive(options.cellTtlSeconds, 120),
      stateEmitIntervalSeconds: positive(options.stateEmitIntervalSeconds, 5),
      minAttractorStability: clamp01(finite(options.minAttractorStability, 0.48)),
      minAttractorOccurrences: Math.max(2, Math.min(128, Math.trunc(positive(options.minAttractorOccurrences, 2)))),
      onEvent: options.onEvent,
    };
  }

  setEventSink(handler?: (event: QuantumVacuumEvent) => void): void {
    this.options.onEvent = handler;
  }

  currentState(): QuantumVacuumState {
    return cloneState(this.state);
  }

  strongestAttractors(limit = 8): QuantumVacuumAttractor[] {
    return [...this.attractors.values()]
      .sort((a, b) =>
        (b.strength * b.stability) - (a.strength * a.stability) ||
        a.id.localeCompare(b.id)
      )
      .slice(0, Math.max(0, Math.trunc(limit)))
      .map(cloneAttractor);
  }

  spatialCells(limit = 32): QuantumVacuumCell[] {
    return [...this.cells.values()]
      .sort((a, b) => b.energy - a.energy || a.id.localeCompare(b.id))
      .slice(0, Math.max(0, Math.trunc(limit)))
      .map(cloneCell);
  }

  sampleAt(position: SpectrumVec3): QuantumVacuumCell | undefined {
    const cell = this.cells.get(this.cellId(position));
    return cell ? cloneCell(cell) : undefined;
  }

  update(input: QuantumVacuumUpdateInput): QuantumVacuumState {
    const atSeconds = Math.max(0, finite(input.atSeconds));
    const modes = [...input.modes]
      .filter(mode => mode.frequency > 0 && mode.amplitude > 0)
      .sort((a, b) =>
        (b.amplitude * b.stability) - (a.amplitude * a.stability) ||
        a.id.localeCompare(b.id)
      )
      .slice(0, this.options.maxModesPerPass);

    const emitters = [...(input.emitters ?? [])]
      .filter(emitter => emitter.amplitude > 0)
      .sort((a, b) => a.id.localeCompare(b.id))
      .slice(0, this.options.maxEmittersPerPass);

    this.observeAttractors(modes, atSeconds);
    this.observeCells(emitters, atSeconds);
    this.prune(atSeconds);

    const previous = this.state;
    const next = this.deriveState(modes, atSeconds);
    this.state = next;

    if (this.initialized) this.detectTransitions(previous, next);
    else this.initialized = true;

    if (atSeconds - this.lastStateEmitAt >= this.options.stateEmitIntervalSeconds) {
      this.lastStateEmitAt = atSeconds;
      this.options.onEvent?.({
        type: "vacuum.state",
        atSeconds,
        payload: cloneState(next),
      });
    }
    return cloneState(next);
  }

  proposals(limit = 8): QuantumVacuumProposal[] {
    const state = this.state;
    const confidence = clamp01(
      Math.min(1, state.activeModes / 24) * 0.45 +
      state.attractorStrength * 0.35 +
      state.coherence * 0.2
    );
    const values: QuantumVacuumProposal[] = [];

    if (state.coherence > 0.62) {
      values.push({
        channel: "visual",
        effect: "vacuum:coherence-bloom",
        intensity: clamp01((state.coherence - 0.62) / 0.38),
        confidence,
        reason: "coherent spectral modes are phase-aligning",
      });
      values.push({
        channel: "audio",
        effect: "vacuum:phase-lock",
        intensity: clamp01(state.phaseAlignment * state.coherence),
        confidence,
        reason: "global phase vector is becoming aligned",
      });
    }
    if (state.entropy > 0.55) {
      values.push({
        channel: "weather",
        effect: "vacuum:entropy-turbulence",
        intensity: clamp01((state.entropy - 0.55) / 0.45),
        confidence,
        reason: "spectral entropy is elevated",
      });
    }
    if (state.cascadePressure > 0.15) {
      values.push({
        channel: "system",
        effect: "vacuum:cascade-density",
        intensity: clamp01(state.cascadePressure),
        confidence,
        reason: "multi-generation Ω cascades are active",
      });
    }
    if (state.symmetryBreak > 0.25) {
      values.push({
        channel: "navigation",
        effect: "vacuum:asymmetry-bias",
        intensity: clamp01(state.symmetryBreak),
        confidence,
        reason: "spectral energy is asymmetrically distributed",
      });
    }
    if (state.attractorStrength > 0.5) {
      values.push({
        channel: "material",
        effect: "vacuum:attractor-memory",
        intensity: clamp01(state.attractorStrength),
        confidence,
        reason: "recurring spectral attractors are persisting",
      });
    }
    if (state.energyDensity > 0.65) {
      values.push({
        channel: "physics",
        effect: "vacuum:field-pressure",
        intensity: clamp01((state.energyDensity - 0.65) / 0.35),
        confidence,
        reason: "vacuum energy density is high",
      });
    }

    return values
      .sort((a, b) => b.intensity - a.intensity || a.effect.localeCompare(b.effect))
      .slice(0, Math.max(0, Math.trunc(limit)));
  }

  snapshot(): QuantumVacuumSnapshot {
    return {
      version: 1,
      state: cloneState(this.state),
      attractors: [...this.attractors.values()].map(cloneAttractor),
      cells: [...this.cells.values()].map(cloneCell),
      transitionSerial: this.transitionSerial,
      lastStateEmitAt: this.lastStateEmitAt,
      initialized: this.initialized,
    };
  }

  restore(snapshot: unknown): { attractors: number; cells: number } {
    if (!snapshot || typeof snapshot !== "object") return { attractors: 0, cells: 0 };
    const candidate = snapshot as Partial<QuantumVacuumSnapshot>;
    if (candidate.version !== 1) return { attractors: 0, cells: 0 };

    this.attractors.clear();
    for (const attractor of candidate.attractors ?? []) {
      if (!attractor?.id || !(attractor.centroidFrequency > 0)) continue;
      this.attractors.set(attractor.id, cloneAttractor(attractor));
    }

    this.cells.clear();
    for (const cell of candidate.cells ?? []) {
      if (!cell?.id || !cell.center) continue;
      this.cells.set(cell.id, cloneCell(cell));
    }

    this.state = candidate.state ? cloneState(candidate.state) : initialState();
    this.transitionSerial = Math.max(0, Math.trunc(candidate.transitionSerial ?? 0));
    this.lastStateEmitAt = finite(candidate.lastStateEmitAt, -1e15);
    this.initialized = Boolean(candidate.initialized);
    return { attractors: this.attractors.size, cells: this.cells.size };
  }

  clear(): void {
    this.attractors.clear();
    this.cells.clear();
    this.state = initialState();
    this.transitionSerial = 0;
    this.lastStateEmitAt = -1e15;
    this.initialized = false;
  }

  private deriveState(
    modes: readonly EmergentSpectralMode[],
    atSeconds: number,
  ): QuantumVacuumState {
    if (!modes.length) {
      return {
        ...initialState(),
        atSeconds,
        activeAttractors: this.attractors.size,
        activeCells: this.cells.size,
      };
    }

    let weight = 0;
    let energy = 0;
    let coherence = 0;
    let entropy = 0;
    let resonance = 0;
    let novelty = 0;
    let cascade = 0;
    let phaseX = 0;
    let phaseY = 0;
    let logFrequency = 0;

    for (const mode of modes) {
      const w = Math.max(0.000001, mode.amplitude);
      weight += w;
      energy += mode.amplitude * mode.amplitude;
      coherence += mode.coherence * w;
      entropy += ((1 - mode.coherence) * 0.55 + mode.novelty * 0.45) * w;
      resonance += (mode.occurrences >= 2 ? mode.stability : 0) * w;
      novelty += mode.novelty * w;
      if (mode.family === "cascade") {
        cascade += mode.amplitude * mode.stability *
          Math.min(1, 0.35 + mode.generation / 3);
      }
      phaseX += Math.cos(mode.phase) * w;
      phaseY += Math.sin(mode.phase) * w;
      logFrequency += Math.log2(Math.max(Number.EPSILON, mode.frequency)) * w;
    }

    const meanLogFrequency = logFrequency / weight;
    let low = 0;
    let high = 0;
    for (const mode of modes) {
      const spectralWeight = mode.amplitude * mode.stability;
      if (Math.log2(Math.max(Number.EPSILON, mode.frequency)) < meanLogFrequency) {
        low += spectralWeight;
      } else {
        high += spectralWeight;
      }
    }

    const attractorStrength = this.strongestAttractors(1)[0]?.strength ?? 0;
    return {
      atSeconds,
      energyDensity: clamp01(energy / Math.max(1, Math.sqrt(modes.length) * 1.6)),
      coherence: clamp01(coherence / weight),
      entropy: clamp01(entropy / weight),
      resonanceDensity: clamp01(resonance / weight),
      noveltyFlux: clamp01(novelty / weight),
      cascadePressure: clamp01(cascade / Math.max(1, Math.sqrt(modes.length))),
      phaseAlignment: clamp01(Math.hypot(phaseX, phaseY) / weight),
      symmetryBreak: clamp01(Math.abs(high - low) / Math.max(Number.EPSILON, high + low)),
      attractorStrength: clamp01(attractorStrength),
      activeModes: modes.length,
      activeAttractors: this.attractors.size,
      activeCells: this.cells.size,
    };
  }

  private observeAttractors(
    modes: readonly EmergentSpectralMode[],
    atSeconds: number,
  ): void {
    for (const mode of modes) {
      if (
        mode.stability < this.options.minAttractorStability ||
        mode.occurrences < this.options.minAttractorOccurrences
      ) continue;

      const logFrequency = Math.log2(Math.max(Number.EPSILON, mode.frequency));
      const bucket = Math.round(
        logFrequency / this.options.attractorResolutionOctaves
      ) * this.options.attractorResolutionOctaves;
      const key = `${mode.family}:${bucket.toFixed(6)}`;
      const id = "QV-" + hash32(key).toString(36).toUpperCase().padStart(7, "0");
      const strength = clamp01(
        mode.amplitude * mode.stability * (1 - mode.novelty * 0.25)
      );
      const current = this.attractors.get(id);

      if (!current) {
        const attractor: QuantumVacuumAttractor = {
          id,
          bucket,
          centroidFrequency: mode.frequency,
          strength,
          coherence: mode.coherence,
          stability: mode.stability,
          evidence: 1,
          firstSeenAt: atSeconds,
          lastSeenAt: atSeconds,
          modeIds: [mode.id],
        };
        this.attractors.set(id, attractor);
        this.options.onEvent?.({
          type: "vacuum.attractor.discovered",
          atSeconds,
          payload: cloneAttractor(attractor),
        });
        continue;
      }

      current.evidence += 1;
      current.lastSeenAt = atSeconds;
      current.centroidFrequency =
        current.centroidFrequency * 0.8 + mode.frequency * 0.2;
      current.strength = clamp01(current.strength * 0.78 + strength * 0.22);
      current.coherence = clamp01(current.coherence * 0.82 + mode.coherence * 0.18);
      current.stability = clamp01(current.stability * 0.85 + mode.stability * 0.15);
      current.modeIds = [...new Set([...current.modeIds, mode.id])].sort().slice(-24);

      if ((current.evidence & (current.evidence - 1)) === 0) {
        this.options.onEvent?.({
          type: "vacuum.attractor.reinforced",
          atSeconds,
          payload: cloneAttractor(current),
        });
      }
    }
  }

  private observeCells(
    emitters: readonly SpectralEmitter[],
    atSeconds: number,
  ): void {
    const aggregate = new Map<string, {
      center: SpectrumVec3;
      energy: number;
      weightedCoherence: number;
      weightedEntropy: number;
      weightedNonlinear: number;
      weight: number;
      sourceCount: number;
    }>();

    for (const emitter of emitters) {
      if (!emitter.position) continue;
      const id = this.cellId(emitter.position);
      const current = aggregate.get(id) ?? {
        center: this.cellCenter(emitter.position),
        energy: 0,
        weightedCoherence: 0,
        weightedEntropy: 0,
        weightedNonlinear: 0,
        weight: 0,
        sourceCount: 0,
      };
      const weight = Math.max(0.000001, emitter.amplitude);
      current.energy += emitter.amplitude;
      current.weightedCoherence += emitter.coherence * weight;
      current.weightedEntropy += emitter.entropy * weight;
      current.weightedNonlinear += emitter.nonlinearGain * weight;
      current.weight += weight;
      current.sourceCount += 1;
      aggregate.set(id, current);
    }

    for (const [id, value] of aggregate) {
      const energy = clamp01(1 - Math.exp(-value.energy * 0.55));
      const coherence = clamp01(value.weightedCoherence / value.weight);
      const entropy = clamp01(value.weightedEntropy / value.weight);
      const nonlinearPressure = clamp01(value.weightedNonlinear / value.weight);
      const current = this.cells.get(id);
      if (!current) {
        this.cells.set(id, {
          id,
          center: value.center,
          energy,
          coherence,
          entropy,
          nonlinearPressure,
          sourceCount: value.sourceCount,
          lastSeenAt: atSeconds,
        });
        continue;
      }
      current.energy = clamp01(current.energy * 0.65 + energy * 0.35);
      current.coherence = clamp01(current.coherence * 0.7 + coherence * 0.3);
      current.entropy = clamp01(current.entropy * 0.7 + entropy * 0.3);
      current.nonlinearPressure = clamp01(
        current.nonlinearPressure * 0.7 + nonlinearPressure * 0.3
      );
      current.sourceCount = value.sourceCount;
      current.lastSeenAt = atSeconds;
    }
  }

  private detectTransitions(
    from: QuantumVacuumState,
    to: QuantumVacuumState,
  ): void {
    const transitions: Array<[VacuumPhaseTransitionKind, number]> = [];
    if (to.coherence >= 0.78 && from.coherence < 0.68) {
      transitions.push(["coherence-lock", to.coherence]);
    }
    if (to.entropy >= 0.68 && from.entropy < 0.58) {
      transitions.push(["entropy-release", to.entropy]);
    }
    if (to.cascadePressure >= 0.28 && from.cascadePressure < 0.18) {
      transitions.push(["cascade-bloom", to.cascadePressure]);
    }
    if (to.symmetryBreak >= 0.42 && from.symmetryBreak < 0.3) {
      transitions.push(["symmetry-break", to.symmetryBreak]);
    }
    if (to.resonanceDensity >= 0.7 && from.resonanceDensity < 0.55) {
      transitions.push(["resonance-lock", to.resonanceDensity]);
    }
    if (to.energyDensity <= 0.08 && from.energyDensity > 0.18) {
      transitions.push(["vacuum-quiet", 1 - to.energyDensity]);
    }

    for (const [kind, intensity] of transitions) {
      const transition: QuantumVacuumTransition = {
        id: `QVT-${(++this.transitionSerial).toString(36).toUpperCase().padStart(6, "0")}`,
        kind,
        atSeconds: to.atSeconds,
        intensity: clamp01(intensity),
        from: cloneState(from),
        to: cloneState(to),
      };
      this.options.onEvent?.({
        type: "vacuum.phase-transition",
        atSeconds: to.atSeconds,
        payload: transition,
      });
    }
  }

  private prune(atSeconds: number): void {
    for (const [id, attractor] of this.attractors) {
      if (atSeconds - attractor.lastSeenAt > this.options.attractorTtlSeconds) {
        this.attractors.delete(id);
      }
    }
    if (this.attractors.size > this.options.maxAttractors) {
      const trim = [...this.attractors.values()].sort((a, b) =>
        (a.strength * a.stability) - (b.strength * b.stability) ||
        a.lastSeenAt - b.lastSeenAt
      );
      const remove = this.attractors.size - this.options.maxAttractors;
      for (let index = 0; index < remove; index += 1) {
        this.attractors.delete(trim[index].id);
      }
    }

    for (const [id, cell] of this.cells) {
      if (atSeconds - cell.lastSeenAt > this.options.cellTtlSeconds) {
        this.cells.delete(id);
      }
    }
    if (this.cells.size > this.options.maxCells) {
      const trim = [...this.cells.values()].sort((a, b) =>
        a.energy - b.energy || a.lastSeenAt - b.lastSeenAt
      );
      const remove = this.cells.size - this.options.maxCells;
      for (let index = 0; index < remove; index += 1) {
        this.cells.delete(trim[index].id);
      }
    }
  }

  private cellId(position: SpectrumVec3): string {
    const size = this.options.cellSize;
    const x = Math.floor(finite(position.x) / size);
    const y = Math.floor(finite(position.y) / size);
    const z = Math.floor(finite(position.z) / size);
    return `qvc:${x}:${y}:${z}`;
  }

  private cellCenter(position: SpectrumVec3): SpectrumVec3 {
    const size = this.options.cellSize;
    const center = (value: number) =>
      (Math.floor(finite(value) / size) + 0.5) * size;
    return {
      x: center(position.x),
      y: center(position.y),
      z: center(position.z ?? 0),
    };
  }
}
